import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaBan,
  FaBell,
  FaCopy,
  FaDownload,
  FaEnvelope,
  FaHandHoldingUsd,
  FaPaperPlane,
  FaPen,
  FaTrash,
  FaWhatsapp,
} from "react-icons/fa";
import { useConfirm, useNotify } from "../../components/ConfirmModal";
import { Modal, PageHead, usePrompt } from "../../pages/work/WorkUi";
import PortalSelect from "../PortalSelect";
import { formatPhoneDisplay } from "../../utils/phone";
import billingApi, { billingError } from "./billingApi";
import {
  InvoiceStatus,
  clientInvoicePdfBase64,
  downloadClientInvoicePdf,
  fmtDate,
  invoiceMessage,
  money,
  todayIso,
  useBillingMeta,
  whatsappLink,
} from "./BillingUi";
import "../../pages/dm/dm.css";
import "../../pages/work/work.css";
import "./billing.css";

function PaymentModal({ inv, modes, onClose, onSaved }) {
  const notify = useNotify();
  const [form, setForm] = useState({
    Amount: inv.Balance_Amount,
    Payment_Date: todayIso(),
    Payment_Mode_Id: "",
    Reference: "",
    Notes: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await billingApi.recordPayment(inv.Invoice_Id, form);
      notify.success(res.Status === "Paid" ? "Payment recorded. The invoice is fully paid." : "Payment recorded");
      onSaved(res);
    } catch (err) {
      notify.error(billingError(err, "Could not record the payment"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Record payment · ${inv.Invoice_No}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="dmp-btn dmp-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="bl-payment" className="dmp-btn" disabled={saving}>
            Save payment
          </button>
        </>
      }
    >
      <form id="bl-payment" onSubmit={save} className="bl-form-grid">
        <label className="wk-field">
          Amount received
          <input type="number" min="0.01" step="0.01" max={inv.Balance_Amount} value={form.Amount} onChange={set("Amount")} required />
          <span className="wk-field__hint">Balance due: {money(inv.Balance_Amount, inv.Currency)}</span>
        </label>
        <label className="wk-field">
          Date received
          <input type="date" max={todayIso()} value={form.Payment_Date} onChange={set("Payment_Date")} required />
        </label>
        <label className="wk-field">
          Mode
          <PortalSelect value={form.Payment_Mode_Id} onChange={set("Payment_Mode_Id")} aria-label="Payment mode">
            <option value="">Pick a mode</option>
            {modes.map((m) => (
              <option key={m.Payment_Mode_Id} value={m.Payment_Mode_Id}>
                {m.Name}
              </option>
            ))}
          </PortalSelect>
        </label>
        <label className="wk-field">
          Reference (UTR, cheque no.)
          <input value={form.Reference} onChange={set("Reference")} maxLength={150} />
        </label>
        <label className="wk-field bl-span-2">
          Note (optional)
          <input value={form.Notes} onChange={set("Notes")} maxLength={500} />
        </label>
      </form>
    </Modal>
  );
}

function SendModal({ inv, onClose, onSent }) {
  const notify = useNotify();
  const [to, setTo] = useState(inv.Bill_To?.Email || "");
  const [subject, setSubject] = useState(`Invoice ${inv.Invoice_No} from ${inv.Issuer?.Name || ""}`.trim());
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const pdf = await clientInvoicePdfBase64(inv);
      const res = await billingApi.sendInvoice(inv.Invoice_Id, { to, subject, message: message || undefined, pdf_base64: pdf });
      notify.success(`Invoice emailed to ${res.sent_to.join(", ")}`);
      onSent(res.invoice);
    } catch (err) {
      notify.error(billingError(err, "Could not send the email"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      title="Email the invoice"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="dmp-btn dmp-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="bl-send" className="dmp-btn" disabled={sending || !to.trim()}>
            <FaPaperPlane aria-hidden="true" /> {sending ? "Sending…" : "Send"}
          </button>
        </>
      }
    >
      <form id="bl-send" onSubmit={send}>
        <label className="wk-field">
          To
          <input type="text" value={to} onChange={(e) => setTo(e.target.value)} placeholder="client@example.com" required />
          <span className="wk-field__hint">Separate more than one address with commas. Sent from your company email (Settings).</span>
        </label>
        <label className="wk-field">
          Subject
          <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} />
        </label>
        <label className="wk-field">
          Message (optional)
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder="A short note is added automatically when this is empty." />
        </label>
        <p className="wk-muted" style={{ fontSize: 13 }}>
          The invoice PDF ({inv.Invoice_No}.pdf) is attached.
        </p>
      </form>
    </Modal>
  );
}

/** One client invoice: details, payments, and actions (record payment, send, PDF, WhatsApp, cancel, duplicate). */
function TenantClientInvoiceView() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const confirm = useConfirm();
  const [promptUi, ask] = usePrompt();
  const { meta } = useBillingMeta();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(params.get("share") === "1" ? "send" : null);
  const canManage = !!meta?.access?.manage;

  const load = useCallback(async () => {
    try {
      setInv(await billingApi.invoice(id));
      setError("");
    } catch (err) {
      setError(billingError(err, "Could not load the invoice"));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (params.get("share")) setParams({}, { replace: true });
  }, [params, setParams]);

  const act = async (fn, success) => {
    try {
      const res = await fn();
      if (success) notify.success(success);
      return res;
    } catch (err) {
      notify.error(billingError(err));
      return null;
    }
  };

  if (error) return <div className="dmp wk bl"><div className="dmp-error">{error}</div></div>;
  if (!inv) return <div className="dmp wk bl"><div className="wk-muted">Loading…</div></div>;

  const isDraft = inv.Status === "Draft";
  const isOpen = ["Sent", "Partially Paid"].includes(inv.Status);
  const issued = ["Sent", "Partially Paid", "Paid"].includes(inv.Status);
  const phone = inv.Bill_To?.Phone;

  const issue = async () => {
    if (!(await confirm({ title: "Issue invoice", message: "Issuing gives it the next invoice number and locks it. Continue?", danger: false }))) return;
    const res = await act(() => billingApi.issueInvoice(inv.Invoice_Id), "Invoice issued");
    if (res) {
      setInv(res);
      setModal("send");
    }
  };
  const remove = async () => {
    if (!(await confirm({ title: "Delete draft", message: "Delete this draft invoice?", danger: true }))) return;
    try {
      await billingApi.deleteInvoice(inv.Invoice_Id);
      notify.success("Draft deleted");
      navigate("/portal/finance/invoices");
    } catch (err) {
      notify.error(billingError(err));
    }
  };
  const cancelInvoice = async () => {
    const reason = await ask({
      title: `Cancel ${inv.Invoice_No}`,
      label: "Why is it cancelled?",
      required: true,
      confirmLabel: "Cancel invoice",
      hint: "The number stays on record. Use Duplicate to raise a corrected invoice.",
    });
    if (!reason) return;
    const res = await act(() => billingApi.cancelInvoice(inv.Invoice_Id, reason), "Invoice cancelled");
    if (res) setInv(res);
  };
  const duplicate = async () => {
    const res = await act(() => billingApi.duplicateInvoice(inv.Invoice_Id), "Copied as a new draft");
    if (res) navigate(`/portal/finance/client-invoices/${res.Invoice_Id}/edit`);
  };
  const cancelPayment = async (p) => {
    if (!(await confirm({ title: "Cancel payment", message: `Cancel the payment of ${money(p.Amount, inv.Currency)} on ${fmtDate(p.Payment_Date)}?`, danger: true }))) return;
    const res = await act(() => billingApi.cancelPayment(p.Payment_Id), "Payment cancelled");
    if (res) setInv(res);
  };

  return (
    <div className="dmp wk bl">
      {promptUi}
      <Link to="/portal/finance/invoices" className="dmp-back-link">
        <FaArrowLeft aria-hidden="true" /> Invoices
      </Link>
      <PageHead title={inv.Invoice_No ? `Invoice ${inv.Invoice_No}` : "Draft invoice"}>
        <InvoiceStatus status={inv.Display_Status} small={false} />
      </PageHead>

      <div className="wk-actions-bar bl-actions">
        {canManage && isDraft ? (
          <>
            <Link className="dmp-btn dmp-btn--ghost" to={`/portal/finance/client-invoices/${inv.Invoice_Id}/edit`}>
              <FaPen aria-hidden="true" /> Edit
            </Link>
            <button type="button" className="dmp-btn" onClick={issue}>
              <FaPaperPlane aria-hidden="true" /> Issue
            </button>
          </>
        ) : null}
        {canManage && isOpen ? (
          <button type="button" className="dmp-btn" onClick={() => setModal("payment")}>
            <FaHandHoldingUsd aria-hidden="true" /> Record payment
          </button>
        ) : null}
        {canManage && issued ? (
          <button type="button" className="dmp-btn dmp-btn--ghost" onClick={() => setModal("send")}>
            <FaEnvelope aria-hidden="true" /> {inv.Sent_On ? "Email again" : "Email"}
          </button>
        ) : null}
        {issued ? (
          <a className="dmp-btn dmp-btn--ghost" href={whatsappLink(phone, invoiceMessage(inv, inv.Is_Overdue))} target="_blank" rel="noreferrer">
            {inv.Is_Overdue ? <FaBell aria-hidden="true" /> : <FaWhatsapp aria-hidden="true" />}
            {inv.Is_Overdue ? " Remind on WhatsApp" : " WhatsApp"}
          </a>
        ) : null}
        <button type="button" className="dmp-btn dmp-btn--ghost" onClick={() => downloadClientInvoicePdf(inv)}>
          <FaDownload aria-hidden="true" /> PDF
        </button>
        {canManage && !isDraft ? (
          <button type="button" className="dmp-btn dmp-btn--ghost" onClick={duplicate}>
            <FaCopy aria-hidden="true" /> Duplicate
          </button>
        ) : null}
        {canManage && isDraft ? (
          <button type="button" className="dmp-btn dmp-btn--danger" onClick={remove}>
            <FaTrash aria-hidden="true" /> Delete
          </button>
        ) : null}
        {canManage && issued ? (
          <button type="button" className="dmp-btn dmp-btn--danger" onClick={cancelInvoice}>
            <FaBan aria-hidden="true" /> Cancel
          </button>
        ) : null}
      </div>

      {inv.Status === "Cancelled" ? (
        <div className="wk-banner" role="status">
          Cancelled on {fmtDate(inv.Cancelled_On)}
          {inv.Cancel_Reason ? `: ${inv.Cancel_Reason}` : ""}
        </div>
      ) : null}

      <div className="wk-grid-2">
        <div className="dmp-card">
          <div className="bl-parties">
            <div>
              <div className="bl-label">From</div>
              <strong>{inv.Issuer?.Name}</strong>
              <div className="wk-muted">{[inv.Issuer?.Address, inv.Issuer?.Phone, inv.Issuer?.Email].filter(Boolean).join(" · ")}</div>
              {inv.Issuer?.GSTIN ? <div className="wk-muted">GSTIN {inv.Issuer.GSTIN}</div> : null}
            </div>
            <div>
              <div className="bl-label">Bill to</div>
              <strong>{inv.Bill_To?.Name}</strong>
              <div className="wk-muted">
                {[inv.Bill_To?.Contact, inv.Bill_To?.Email, formatPhoneDisplay("", inv.Bill_To?.Phone)].filter(Boolean).join(" · ")}
              </div>
              {inv.Bill_To?.Address ? <div className="wk-muted">{inv.Bill_To.Address}</div> : null}
              {inv.Bill_To?.GSTIN ? <div className="wk-muted">GSTIN {inv.Bill_To.GSTIN}</div> : null}
            </div>
          </div>
          <table className="wk-table bl-lines bl-lines--view">
            <thead>
              <tr>
                <th>Item or service</th>
                <th className="bl-num">Qty</th>
                <th className="bl-num">Rate</th>
                <th className="bl-num">{inv.Issuer?.Tax_Label || "Tax"}</th>
                <th className="bl-num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {inv.items.map((it) => (
                <tr key={it.Item_Id}>
                  <td data-label="Item">{it.Description}</td>
                  <td data-label="Qty" className="bl-num">{Number(it.Quantity)}</td>
                  <td data-label="Rate" className="bl-num">{money(it.Unit_Price, inv.Currency)}</td>
                  <td data-label="Tax" className="bl-num">
                    {it.Tax_Pct ? `${Number(it.Tax_Pct)}% · ${money(it.Tax_Amount, inv.Currency)}` : "—"}
                  </td>
                  <td data-label="Amount" className="bl-num">{money(it.Total_Amount, inv.Currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="bl-sum">
            <div><span>Subtotal</span><span>{money(inv.Sub_Total, inv.Currency)}</span></div>
            <div><span>{inv.Issuer?.Tax_Label || "Tax"}</span><span>{money(inv.Tax_Amount, inv.Currency)}</span></div>
            {Number(inv.Discount_Amount) ? (
              <div><span>Discount</span><span>-{money(inv.Discount_Amount, inv.Currency)}</span></div>
            ) : null}
            <div className="bl-sum__total"><span>Total</span><span>{money(inv.Total_Amount, inv.Currency)}</span></div>
            <div><span>Paid</span><span>{money(inv.Paid_Amount, inv.Currency)}</span></div>
            <div className="bl-sum__total"><span>Balance due</span><span>{money(inv.Balance_Amount, inv.Currency)}</span></div>
          </div>
          {inv.Notes ? <p><strong>Notes:</strong> {inv.Notes}</p> : null}
          {inv.Terms ? <p className="wk-muted"><strong>Terms:</strong> {inv.Terms}</p> : null}
        </div>

        <div>
          <div className="dmp-card">
            <h3 className="dmp-card__title">Details</h3>
            <dl className="wk-kv">
              <dt>Invoice date</dt>
              <dd>{fmtDate(inv.Invoice_Date)}</dd>
              <dt>Due</dt>
              <dd>
                {fmtDate(inv.Due_Date)}
                {inv.Is_Overdue ? <span className="bl-late"> · {inv.Days_Overdue} days late</span> : null}
              </dd>
              {inv.Project_Name || inv.Service_Name ? (
                <>
                  <dt>{inv.Project_Name ? "Project" : "Service"}</dt>
                  <dd>{inv.Project_Name || inv.Service_Name}</dd>
                </>
              ) : null}
              <dt>Emailed</dt>
              <dd>{inv.Sent_On ? fmtDate(inv.Sent_On) : "Not yet"}</dd>
            </dl>
            <Link to={`/portal/clients/${inv.Tenant_Client_Id}?tab=invoices`} className="dmp-link">
              All invoices of {inv.Bill_To?.Name || "this client"}
            </Link>
          </div>
          <div className="dmp-card">
            <h3 className="dmp-card__title">Payments</h3>
            {inv.payments.length ? (
              <ul className="bl-payments">
                {inv.payments.map((p) => (
                  <li key={p.Payment_Id} className={p.Status === "Cancelled" ? "is-cancelled" : ""}>
                    <div>
                      <strong>{money(p.Amount, inv.Currency)}</strong> · {fmtDate(p.Payment_Date)}
                      <div className="wk-muted" style={{ fontSize: 12 }}>
                        {[p.Payment_Mode, p.Reference, p.Notes].filter(Boolean).join(" · ") || "No details"}
                        {p.Status === "Cancelled" ? " · cancelled" : ""}
                      </div>
                    </div>
                    {canManage && p.Status === "Completed" && inv.Status !== "Cancelled" ? (
                      <button type="button" className="dmp-link" onClick={() => cancelPayment(p)}>
                        Cancel
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="wk-muted">No payments yet.</p>
            )}
          </div>
        </div>
      </div>

      {modal === "payment" ? (
        <PaymentModal
          inv={inv}
          modes={meta?.payment_modes || []}
          onClose={() => setModal(null)}
          onSaved={(res) => {
            setInv(res);
            setModal(null);
          }}
        />
      ) : null}
      {modal === "send" && issued && canManage ? (
        <SendModal
          inv={inv}
          onClose={() => setModal(null)}
          onSent={(res) => {
            setInv(res);
            setModal(null);
          }}
        />
      ) : null}
    </div>
  );
}

export default TenantClientInvoiceView;
